import { useMutation, useQuery } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import { useEffect, useMemo, useState } from "react";

import { DISCREPANCY_MUTATION_REFETCH_QUERIES } from "./discrepancyRefetchQueries";
import { type ActionKey } from "./DiscrepancyReviewDialog.helpers";
import {
  DISCREPANCIES_MATCHING_QUERY,
  type MatchingDiscrepancyRow,
  type ResolveActionOption,
} from "./ResolveDiscrepanciesDialog";
import { CORRECT_VALUE_SOURCE, RESOLUTION_TARGET } from "./resolveMutationEnums";
import { eligibleOtherIdsForSource } from "./resolveWithValue.applyToOthers";
import {
  answerValueForRow,
  sharedRowValue,
  buildResolveWithValueInput,
  computeCanApplyToOthers,
  computeDialogSaveDisabled,
  computeDocumentWriteState,
  computeEManifestWriteState,
  computeManualValue,
  computeShowApplyToOthers,
  computeShowOpenDocumentLink,
  computeVendorWriteState,
  derivePreviewState,
  dismissDisabledReason,
  formatDocumentBarLabel,
  formatResolveTitle,
  documentValueDisabledReason,
  isValueSourcedAnswer,
  otherValueDisabledReason,
  valueAnswersDisabledReason,
  type Q1Answer,
  type ResolutionTargetLiteral,
  type PreviewState,
  type ResolveRow,
} from "./resolveWithValue.helpers";
import {
  countOutcomes,
  formatStatusAfterSave,
  predictRowOutcome,
} from "./resolveWithValue.outcome";
import { singleRowSaveToast } from "./resolveWithValue.saveToast";

export const RESOLVE_DISCREPANCIES = gql(`
  mutation ResolveDiscrepancies($input: ResolveDiscrepanciesInput!) {
    resolveDiscrepancies(input: $input) {
      id
      processedManifestId
      field
      status
      eManifestValue
      vendorValue
      vendorDiscrepant
      manifestDocumentValue
      documentDiscrepant
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionPath
      resolutionNote
    }
  }
`);

/** Which of the matching ("apply to others") rows the chosen answer would actually write, mirroring the primary rows' own eligibility rule for that answer. */
function otherEligibleIdsFor(
  answer: Q1Answer,
  matchingRows: readonly MatchingDiscrepancyRow[],
  writesVendor: boolean,
  writesDocument: boolean,
  computeActionEligibleIds: (action: "nonDiscrepancy" | "cantDetermine") => string[]
): string[] {
  if (isValueSourcedAnswer(answer)) return [];
  if (answer === "DISMISS") return computeActionEligibleIds("nonDiscrepancy");
  if (answer === "CANT_DETERMINE") return computeActionEligibleIds("cantDetermine");
  if (answer !== "EMANIFEST" && answer !== "VENDOR") return [];
  // EMANIFEST / VENDOR: each matching row must pass the server's checks for the written targets.
  return eligibleOtherIdsForSource(answer, matchingRows, writesVendor, writesDocument);
}

/** The reviewer's free-form answer state: which radio, the typed/chip value, the note, and "apply to others". Reset whenever the dialog closes. */
function useAnswerFormState(open: boolean) {
  const [answer, setAnswer] = useState<Q1Answer | null>(null);
  const [manualText, setManualText] = useState("");
  const [manualCodes, setManualCodes] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [applyToOthers, setApplyToOthers] = useState(false);

  useEffect(() => {
    if (open) return;
    setAnswer(null);
    setManualText("");
    setManualCodes([]);
    setNote("");
    setApplyToOthers(false);
  }, [open]);

  return {
    answer,
    setAnswer,
    manualText,
    setManualText,
    manualCodes,
    setManualCodes,
    note,
    setNote,
    applyToOthers,
    setApplyToOthers,
  };
}

/**
 * The Preview's derived state: the typed/chip value in its stored-string
 * shape, the per-row answer accessor, and (via derivePreviewState) the
 * per-source write states, summary line, and inline validation message.
 */
function usePreviewDerived(args: {
  rows: readonly ResolveRow[];
  singleRow: ResolveRow | undefined;
  answer: Q1Answer | null;
  manualText: string;
  manualCodes: string[];
}): PreviewState & { manualValue: string } {
  const { rows, singleRow, answer, manualText, manualCodes } = args;

  const manualValue = useMemo(
    () => (singleRow ? computeManualValue(singleRow.field, manualText, manualCodes) : ""),
    [singleRow, manualText, manualCodes]
  );

  const getAnswerValue = useMemo(() => {
    if (!answer) return () => null;
    return (row: ResolveRow) => answerValueForRow(answer, row, manualValue);
  }, [answer, manualValue]);

  const vendorDefault = useMemo(
    () =>
      answer ? computeVendorWriteState(rows, isValueSourcedAnswer(answer), getAnswerValue) : null,
    [rows, answer, getAnswerValue]
  );
  const eManifestDefault = useMemo(
    () => (answer ? computeEManifestWriteState(rows, getAnswerValue) : null),
    [rows, answer, getAnswerValue]
  );
  const documentDefault = useMemo(
    () => (answer ? computeDocumentWriteState(rows, getAnswerValue) : null),
    [rows, answer, getAnswerValue]
  );

  const q2 = derivePreviewState({
    rows,
    answer,
    manualValue,
    vendorDefault,
    eManifestDefault,
    documentDefault,
    getAnswerValue,
  });

  return { ...q2, manualValue };
}

/** The "apply to others" matching-rows query and the answer-eligible subset of it. */
function useMatchingRowsState(args: {
  open: boolean;
  isStaff: boolean;
  totalCount: number;
  singleDiscrepancyId: string | undefined;
  answer: Q1Answer | null;
  writesVendor: boolean;
  writesDocument: boolean;
  computeActionEligibleIds: (
    action: "nonDiscrepancy" | "cantDetermine",
    matchingRows: readonly MatchingDiscrepancyRow[]
  ) => string[];
}) {
  const {
    open,
    isStaff,
    totalCount,
    singleDiscrepancyId,
    answer,
    writesVendor,
    writesDocument,
    computeActionEligibleIds,
  } = args;
  const canApplyToOthers = computeCanApplyToOthers(isStaff, totalCount, singleDiscrepancyId);
  const { data: matchingData, loading: matchingLoading } = useQuery(DISCREPANCIES_MATCHING_QUERY, {
    variables: { discrepancyId: singleDiscrepancyId ?? "" },
    skip: !open || !canApplyToOthers,
  });
  const matchingRows = useMemo(
    () => matchingData?.discrepanciesMatching ?? [],
    [matchingData?.discrepanciesMatching]
  );

  const otherEligibleIds = useMemo(() => {
    if (!answer || matchingRows.length === 0) return [];
    return otherEligibleIdsFor(answer, matchingRows, writesVendor, writesDocument, (action) =>
      computeActionEligibleIds(action, matchingRows)
    );
  }, [answer, matchingRows, writesVendor, writesDocument, computeActionEligibleIds]);
  const otherEligibleRows = useMemo(
    () => matchingRows.filter((r) => otherEligibleIds.includes(r.id)),
    [matchingRows, otherEligibleIds]
  );
  const showApplyToOthers = computeShowApplyToOthers(
    canApplyToOthers,
    matchingLoading,
    matchingRows.length,
    answer
  );

  return { matchingRows, otherEligibleIds, otherEligibleRows, showApplyToOthers };
}

/** The single-row-only handlers: open the document preview, and pick a Q1 answer. */
function useDocumentAndAnswerHandlers(args: {
  singleRow: ResolveRow | undefined;
  setAnswer: (next: Q1Answer) => void;
  onViewDocument:
    | ((documentId: string, fileExtension: string | null | undefined) => void)
    | undefined;
  onClose: () => void;
}) {
  const { singleRow, setAnswer, onViewDocument, onClose } = args;

  const openDocument = () => {
    if (!singleRow?.sourceDocumentId || !onViewDocument) return;
    onViewDocument(singleRow.sourceDocumentId, singleRow.sourceDocumentFileExtension);
    onClose();
  };

  return { openDocument, selectAnswer: setAnswer };
}

/** Builds the Save handler: routes dismiss/block through the parent's onConfirm, otherwise sends one resolveDiscrepancies call. */
function useSaveHandler(args: {
  rows: readonly ResolveRow[];
  answer: Q1Answer | null;
  manualValue: string;
  vendorState: PreviewState["vendorState"];
  eManifestState: PreviewState["eManifestState"];
  documentState: PreviewState["documentState"];
  note: string;
  applyToOthers: boolean;
  otherEligibleIds: string[];
  dismissOption: ResolveActionOption;
  cantDetermineOption: ResolveActionOption | undefined;
  onConfirm: (action: ActionKey, eligibleIds: string[], note: string | null) => void;
  clearSelection: () => void;
  onClose: () => void;
  notifyMutated: () => void;
}) {
  const alerts = useAlerts();
  const [resolve, { loading: resolving }] = useMutation(RESOLVE_DISCREPANCIES, {
    refetchQueries: DISCREPANCY_MUTATION_REFETCH_QUERIES,
  });

  const handleSave = async () => {
    const {
      rows,
      answer,
      manualValue,
      vendorState,
      eManifestState,
      documentState,
      note,
      applyToOthers,
      otherEligibleIds,
      dismissOption,
      cantDetermineOption,
      onConfirm,
      clearSelection,
      onClose,
      notifyMutated,
    } = args;
    if (!answer) return;
    const otherIds = applyToOthers ? otherEligibleIds : [];
    if (answer === "DISMISS") {
      onConfirm("nonDiscrepancy", [...dismissOption.eligibleIds, ...otherIds], note || null);
      return;
    }
    if (answer === "CANT_DETERMINE" && cantDetermineOption) {
      onConfirm("cantDetermine", [...cantDetermineOption.eligibleIds, ...otherIds], note || null);
      return;
    }
    if (!vendorState || !eManifestState) return;
    const writesDocument = documentState?.writes ?? false;
    const input = buildResolveWithValueInput({
      rows,
      answer,
      manualValue,
      writesVendor: vendorState.writes,
      writesEManifest: eManifestState.writes,
      writesDocument,
      note: note || null,
    });
    try {
      const { data } = await resolve({
        variables: {
          input: {
            discrepancyIds: [...input.discrepancyIds, ...otherIds],
            correctValueSource: CORRECT_VALUE_SOURCE[input.correctValueSource],
            correctValue: input.correctValue,
            applyTo: input.applyTo.map((t) => RESOLUTION_TARGET[t]),
            note: input.note,
          },
        },
      });
      const savedStatus = data?.resolveDiscrepancies.find((r) => r.id === rows[0].id)?.status;
      alerts.success(
        singleRowSaveToast(
          rows[0],
          answerValueForRow(answer, rows[0], manualValue),
          input.applyTo,
          savedStatus
        )
      );
      notifyMutated();
      clearSelection();
      onClose();
    } catch (err) {
      alerts.error("Failed to resolve the discrepancy.", err instanceof Error ? err : undefined);
    }
  };

  return { handleSave, resolving };
}

/** Assembles the hook's return value. A plain function (no hooks), so it carries none of its own line-count budget against `useResolveWithValueForm`. */
function buildFormResult(args: {
  rows: readonly ResolveRow[];
  totalCount: number;
  singleRow: ResolveRow | undefined;
  otherValueDisabledReason: string | null;
  documentDisabledReason: string | null;
  loading: boolean;
  resolving: boolean;
  saveDisabled: boolean;
  documentBarLabel: string | null;
  showOpenDocumentLink: boolean;
  valueAnswersDisabledReason: string | null;
  dismissDisabledReason: string | null;
  form: ReturnType<typeof useAnswerFormState>;
  q2: ReturnType<typeof usePreviewDerived>;
  matching: ReturnType<typeof useMatchingRowsState>;
  handlers: ReturnType<typeof useDocumentAndAnswerHandlers>;
  handleSave: () => Promise<void>;
}) {
  const {
    rows,
    totalCount,
    singleRow,
    otherValueDisabledReason,
    documentDisabledReason,
    loading,
    resolving,
    saveDisabled,
    documentBarLabel,
    showOpenDocumentLink,
    valueAnswersDisabledReason,
    dismissDisabledReason,
    form,
    q2,
    matching,
    handlers,
    handleSave,
  } = args;
  return {
    totalCount,
    singleRow,
    answer: form.answer,
    loading,
    resolving,
    otherValueDisabledReason,
    documentDisabledReason,
    valueAnswersDisabledReason,
    dismissDisabledReason,
    manualText: form.manualText,
    manualCodes: form.manualCodes,
    setManualText: form.setManualText,
    setManualCodes: form.setManualCodes,
    selectAnswer: handlers.selectAnswer,
    openDocument: handlers.openDocument,
    handleSave,
    title: formatResolveTitle(rows),
    saveDisabled,
    documentBarLabel,
    showOpenDocumentLink,
    newValue: sharedAnswerValue(rows, form.answer, q2.manualValue),
    statusAfterSave: statusAfterSave(rows, form.answer, q2),
    showsPreview: q2.showsPreview,
    vendorState: q2.vendorState,
    eManifestState: q2.eManifestState,
    documentState: q2.documentState,
    saveDisabledReason: q2.saveDisabledReason,
    saveWarnings: q2.saveWarnings,
    showApplyToOthers: matching.showApplyToOthers,
    matchingRows: matching.matchingRows,
    otherEligibleRows: matching.otherEligibleRows,
    applyToOthers: form.applyToOthers,
    setApplyToOthers: form.setApplyToOthers,
    note: form.note,
    setNote: form.setNote,
  };
}

/** The status save leaves the rows in, from the written sources and each row's answer value. */
function statusAfterSave(
  rows: readonly ResolveRow[],
  answer: Q1Answer | null,
  q2: ReturnType<typeof usePreviewDerived>
): string {
  if (answer == null) return "";
  const applyTo: ResolutionTargetLiteral[] = [];
  if (q2.vendorState?.writes) applyTo.push("VENDOR_DATA");
  if (q2.eManifestState?.writes) applyTo.push("EMANIFEST");
  if (q2.documentState?.writes) applyTo.push("DOCUMENT");
  const outcomes = rows.map((r) =>
    predictRowOutcome(r, answerValueForRow(answer, r, q2.manualValue), applyTo)
  );
  return formatStatusAfterSave(countOutcomes(outcomes));
}

/** The value every targeted row would be set to, or null when the rows would get different values. */
function sharedAnswerValue(
  rows: readonly ResolveRow[],
  answer: Q1Answer | null,
  manualValue: string
): string | null {
  if (answer == null) return null;
  return sharedRowValue(rows, (r) => answerValueForRow(answer, r, manualValue));
}

export interface UseResolveWithValueFormArgs {
  open: boolean;
  rows: ResolveRow[];
  clearSelection: () => void;
  singleDiscrepancyId?: string;
  isStaff: boolean;
  dismissOption: ResolveActionOption;
  cantDetermineOption?: ResolveActionOption;
  otherMutationLoading: boolean;
  onClose: () => void;
  onConfirm: (action: ActionKey, eligibleIds: string[], note: string | null) => void;
  notifyMutated: () => void;
  onViewDocument?: (documentId: string, fileExtension: string | null | undefined) => void;
  /** Injected so this hook doesn't have to import the eligibility helper directly from DiscrepancyReviewDialog.helpers for two call sites. */
  computeActionEligibleIds: (
    action: "nonDiscrepancy" | "cantDetermine",
    matchingRows: readonly MatchingDiscrepancyRow[]
  ) => string[];
}

/**
 * All state, derived values, and handlers for ResolveWithValueDialog. Pulled
 * out of the component and split across the small hooks above so no single
 * function grows past this file's complexity budget.
 */
export function useResolveWithValueForm(fnArgs: UseResolveWithValueFormArgs) {
  const {
    open,
    rows,
    clearSelection,
    singleDiscrepancyId,
    isStaff,
    dismissOption,
    cantDetermineOption,
    otherMutationLoading,
    onClose,
    onConfirm,
    notifyMutated,
    onViewDocument,
    computeActionEligibleIds,
  } = fnArgs;

  const totalCount = rows.length;
  const singleRow = totalCount === 1 ? rows[0] : undefined;

  const form = useAnswerFormState(open);
  const q2 = usePreviewDerived({
    rows,
    singleRow,
    answer: form.answer,
    manualText: form.manualText,
    manualCodes: form.manualCodes,
  });
  const matching = useMatchingRowsState({
    open,
    isStaff,
    totalCount,
    singleDiscrepancyId,
    answer: form.answer,
    writesVendor: q2.vendorState?.writes ?? false,
    writesDocument: q2.documentState?.writes ?? false,
    computeActionEligibleIds,
  });
  const { handleSave, resolving } = useSaveHandler({
    rows,
    answer: form.answer,
    manualValue: q2.manualValue,
    vendorState: q2.vendorState,
    eManifestState: q2.eManifestState,
    documentState: q2.documentState,
    note: form.note,
    applyToOthers: form.applyToOthers,
    otherEligibleIds: matching.otherEligibleIds,
    dismissOption,
    cantDetermineOption,
    onConfirm,
    clearSelection,
    onClose,
    notifyMutated,
  });

  const loading = resolving || otherMutationLoading;
  const documentBarLabel = singleRow ? formatDocumentBarLabel(singleRow) : null;
  const showOpenDocumentLink = computeShowOpenDocumentLink(
    onViewDocument != null,
    singleRow?.sourceDocumentId
  );
  const handlers = useDocumentAndAnswerHandlers({
    singleRow,
    setAnswer: form.setAnswer,
    onViewDocument,
    onClose,
  });

  const saveDisabled = computeDialogSaveDisabled({
    loading,
    answer: form.answer,
    dismissEligibleCount: dismissOption.eligibleIds.length,
    hasCantDetermineOption: cantDetermineOption != null,
    showsPreview: q2.showsPreview,
    saveDisabledReason: q2.saveDisabledReason,
  });

  return buildFormResult({
    rows,
    totalCount,
    singleRow,
    otherValueDisabledReason: otherValueDisabledReason(rows),
    documentDisabledReason: documentValueDisabledReason(rows),
    loading,
    resolving,
    saveDisabled,
    documentBarLabel,
    showOpenDocumentLink,
    valueAnswersDisabledReason: valueAnswersDisabledReason(rows),
    dismissDisabledReason: dismissDisabledReason(dismissOption),
    form,
    q2,
    matching,
    handlers,
    handleSave,
  });
}
